import Modal from "common/Modal/Modal.tsx";
import React, { useEffect, useMemo, useState } from "react";
import RangePicker from "common/DatePicker/RangePicker.tsx";
import styled, { css } from "styled-components";
import Title from "common/Typography/Title.tsx";
import Flex from "common/Boxes/Flex.tsx";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import Select from "common/Select/Select.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces, PayoutMethod } from "@shared/types";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import {
  displayResultMessage,
  getRangePickerValue,
  onTimeframeChange,
} from "utils/misc.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import useAccounts from "features/payouts/Invoices/hooks/useAccounts.ts";
import useDownloadFile from "Global/useDownloadFile.ts";
import dayjs from "dayjs";
import { checkSearch } from "@shared/utils/misc.ts";
import Switch from "common/Buttons/Switch.tsx";
import Text from "common/Typography/Text.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface CreateInvoiceModalProps {
  open: boolean;
  closeModal: () => void;
  paymentMethods?: PayoutMethod[];
}

const CreateInvoiceModal = ({
  open,
  closeModal,
  paymentMethods,
}: CreateInvoiceModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const { downloadFile, isLoading: downloadFileLoading } = useDownloadFile();
  const { activeClientsData } = useAccounts();

  const [selectedAccountsIds, setSelectedAccountsIds] = useState<number[]>([]);
  const [timeframe, setTimeframe] = useState<{
    start: number | null;
    end: number | null;
  }>();
  const [profitSharePercent, setProfitSharePercent] = useState<string | null>();
  const [paymentMethodId, setPaymentMethodId] = useState<number | undefined>();
  const [isVisibleToSubUsers, setIsVisibleToSubUsers] = useState(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [generateInvoice, { isLoading }] =
    sharedLib.sharedApi.settings.useGenerateAssetManagerInvoiceMutation();

  useEffect(() => {
    setPaymentMethodId(
      (current) => current ?? paymentMethods?.[0]?.payout_method_id
    );
  }, [paymentMethods]);

  const accountsOptions = useMemo(
    () =>
      activeClientsData?.map((clientData) => ({
        label: clientData.client_name,
        value: clientData.client_id,
      })),
    [activeClientsData]
  );

  const onShareProfitChange = (value: string) => {
    const numberValue = parseFloat(value);
    if (numberValue < 0) setProfitSharePercent("0");
    else if (numberValue > 100) setProfitSharePercent("100");
    else setProfitSharePercent(value);
  };

  const resetFormData = () => {
    setSelectedAccountsIds([]);
    setTimeframe(undefined);
    setProfitSharePercent(undefined);
    setPaymentMethodId(paymentMethods?.[0]?.payout_method_id);
    setIsVisibleToSubUsers(false);
    setShowErrors(false);
  };

  const handleGenerateInvoice = () => {
    if (
      !timeframe?.start ||
      !timeframe?.end ||
      !profitSharePercent ||
      !paymentMethodId
    ) {
      setShowErrors(true);
      return;
    }

    displayResultMessage(
      generateInvoice({
        period_start: timeframe.start,
        period_end: timeframe.end,
        profit_sharing_percent: parseFloat(profitSharePercent),
        payout_method_id: paymentMethodId,
        client_ids: selectedAccountsIds,
        is_visible_to_sub_users: isVisibleToSubUsers,
      }).unwrap()
    )
      .then((invoice) => {
        const fileName = invoice.pdf_url?.split("/").pop();
        downloadFile(invoice.pdf_url, fileName);
        resetFormData();
        closeModal();
      })
      .catch(() => {});
  };

  return (
    <Modal
      open={open}
      title={t("invoices.create_invoice_modal.title")}
      onOk={handleGenerateInvoice}
      okText={t("invoices.buttons.create")}
      okButtonProps={{ loading: isLoading || downloadFileLoading }}
      onCancel={closeModal}
    >
      <ItemsWrapper>
        <Item>
          <ItemTitle>{t("invoices.create_invoice_modal.accounts")}</ItemTitle>
          <Select
            mode="multiple"
            value={selectedAccountsIds}
            onChange={(value: number[]) => setSelectedAccountsIds(value)}
            options={accountsOptions}
            title={t("invoices.create_invoice_modal.accounts")}
            placeholder={t(
              "invoices.create_invoice_modal.accounts_placeholder"
            )}
            style={{ width: "100%" }}
            showSearch={{
              filterOption: (searchValue, option) =>
                checkSearch(option?.label, searchValue),
            }}
          />
          <RequiredFieldMessage
            requirement={showErrors && !selectedAccountsIds.length}
            errorMessage={t("invoices.create_invoice_modal.errors.accounts")}
            style={ERROR_MESSAGE_STYLE}
          />
        </Item>
        <Item>
          <ItemTitle>{t("invoices.create_invoice_modal.timeframe")}</ItemTitle>
          <RangePicker
            value={getRangePickerValue(timeframe)}
            onChange={(dates) => onTimeframeChange(dates, setTimeframe)}
            wrapperStyle={{ width: "100%" }}
            onPresetsClick={(dates) => onTimeframeChange(dates, setTimeframe)}
            maxValue={dayjs().endOf("day")}
          />
          <RequiredFieldMessage
            requirement={showErrors && (!timeframe?.start || !timeframe?.end)}
            errorMessage={t("invoices.create_invoice_modal.errors.timeframe")}
            style={ERROR_MESSAGE_STYLE}
          />
        </Item>
        <Item>
          <ItemTitle>
            {t("invoices.create_invoice_modal.profit_share_percent")}
          </ItemTitle>
          <CustomInputNumber
            value={profitSharePercent}
            onChange={onShareProfitChange}
            suffix="%"
            precision={1}
            stringMode={true}
            placeholder={t(
              "invoices.create_invoice_modal.profit_share_percent_placeholder"
            )}
          />
          <RequiredFieldMessage
            requirement={showErrors && !profitSharePercent}
            errorMessage={t(
              "invoices.create_invoice_modal.errors.profit_share_precent"
            )}
            style={ERROR_MESSAGE_STYLE}
          />
        </Item>
        <Item>
          <ItemTitle>
            {t("invoices.create_invoice_modal.payment_method")}
          </ItemTitle>
          <Select
            value={paymentMethodId}
            options={paymentMethods?.map((method) => ({
              value: method.payout_method_id,
              label: method.name,
            }))}
            onChange={setPaymentMethodId}
            style={{ width: "100%" }}
            mobileTitle={t("invoices.create_invoice_modal.payment_method")}
          />
          <RequiredFieldMessage
            requirement={showErrors && !paymentMethodId}
            errorMessage={t(
              "invoices.create_invoice_modal.errors.payment_method"
            )}
            style={ERROR_MESSAGE_STYLE}
          />
        </Item>
        <Item>
          <VisibilityControl>
            <ItemTitle>{t("invoices.visibility.show_to_sub_users")}</ItemTitle>
            <Switch
              checked={isVisibleToSubUsers}
              onChange={setIsVisibleToSubUsers}
              aria-label={t("invoices.visibility.show_to_sub_users")}
            />
          </VisibilityControl>
          <Text type="secondary">{t("invoices.visibility.access_hint")}</Text>
        </Item>
      </ItemsWrapper>
    </Modal>
  );
};

export default CreateInvoiceModal;

const ERROR_MESSAGE_STYLE = { marginTop: 4 };
const ItemsWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  row-gap: 24px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      row-gap: 16px;
    `}
`;
const Item = styled.div`
  width: 100%;
`;
const ItemTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Field labels in the label role. */
      && {
        margin-bottom: 6px;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `}
`;
const VisibilityControl = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A setting row: its name (body) level with the switch, the hint
         under it as helper text. */
      && > ${ItemTitle} {
        margin-bottom: 0;
        ${newLookType.body};
        color: ${getNewLookTokens(p.theme).text};
      }
      & + * {
        display: block;
        margin-top: 4px;
        ${newLookType.meta};
        color: ${getNewLookTokens(p.theme).muted};
      }
    `}
`;
